import type { RuleInput } from "./types.js";

/** JS Date.getDay() index (0=Sunday) → ICS weekday code. */
export const JS_DAY_TO_ICS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

/**
 * Minimal RRULE → RuleInput parser covering the rules we emit ourselves.
 * Used both by the task form (to hydrate an existing rule) and by series
 * rescheduling (to derive a fresh rule with a shifted day/month pattern).
 */
export function parseRRule(s: string): RuleInput {
  const parts = Object.fromEntries(
    s.split(";").map((p) => p.split("=")).map(([k, v]) => [k?.toUpperCase() ?? "", v ?? ""]),
  );
  const freq = (parts["FREQ"] ?? "WEEKLY").toLowerCase() as RuleInput["freq"];
  const out: RuleInput = { freq, interval: parts["INTERVAL"] ? Number(parts["INTERVAL"]) : 1 };
  if (parts["BYDAY"]) out.byDay = parts["BYDAY"].split(",") as NonNullable<RuleInput["byDay"]>;
  if (parts["BYMONTHDAY"]) out.byMonthDay = Number(parts["BYMONTHDAY"]);
  if (parts["COUNT"]) out.count = Number(parts["COUNT"]);
  if (parts["UNTIL"]) {
    const d = parseIcsUtc(parts["UNTIL"]);
    if (d) out.until = d;
  }
  return out;
}

/** Parse the YYYYMMDDTHHMMSSZ format emitted by toRRuleString. */
function parseIcsUtc(s: string): Date | null {
  const m = s.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
  if (!m) {
    const fallback = new Date(s);
    return isNaN(fallback.getTime()) ? null : fallback;
  }
  return new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +m[6]!));
}
